import { Link } from "react-router-dom";
import { MapPin, Phone, Mail, Linkedin, Twitter, Instagram } from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_anglin-consult/artifacts/3ph9prb6_Anglin-Sports-Consultancy%20logo.png";

const footerLinks = {
  company: [
    { name: "About Us", path: "/about" },
    { name: "Our Services", path: "/services" },
    { name: "Contact", path: "/contact" },
  ],
  services: [
    { name: "Strategic Consulting", path: "/services#strategic" },
    { name: "Sports & Event Development", path: "/services#sports" },
    { name: "Sponsorship Proposals", path: "/services#sponsorship" },
    { name: "App Development", path: "/services#app" },
  ],
};

export const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer data-testid="footer" className="bg-[#0F172A] border-t border-[#1E293B]">
      {/* Main Footer */}
      <div className="section-container section-padding">
        {/* Big CTA Text */}
        <div className="mb-20">
          <span className="label-accent mb-4 block">Ready to Transform?</span>
          <h2 className="font-heading text-5xl md:text-7xl lg:text-8xl font-bold uppercase tracking-tight text-white leading-none">
            Let's Build
            <br />
            <span className="text-[#E53935]">Something Great</span>
          </h2>
          <Link
            to="/contact"
            data-testid="footer-cta-btn"
            className="btn-primary inline-flex items-center gap-2 mt-8"
          >
            Start a Conversation
          </Link>
        </div>

        {/* Footer Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand Column */}
          <div className="lg:col-span-1">
            <img
              src={LOGO_URL}
              alt="Anglin Sports Consultancy"
              className="h-16 w-auto mb-6"
            />
            <p className="text-slate-400 text-sm leading-relaxed mb-6">
              Premier sports and business consultancy serving the Cayman Islands and beyond. 
              Delivering excellence in strategic consulting, event development, and brand positioning.
            </p>
          </div>

          {/* Company Links */}
          <div>
            <h4 className="font-heading text-lg font-bold uppercase tracking-wider text-white mb-6">
              Company
            </h4>
            <ul className="space-y-3">
              {footerLinks.company.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.path}
                    data-testid={`footer-link-${link.name.toLowerCase().replace(' ', '-')}`}
                    className="text-slate-400 hover:text-[#E53935] transition-colors text-sm"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services Links */}
          <div>
            <h4 className="font-heading text-lg font-bold uppercase tracking-wider text-white mb-6">
              Services
            </h4>
            <ul className="space-y-3">
              {footerLinks.services.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.path}
                    data-testid={`footer-link-${link.name.toLowerCase().replace(' ', '-')}`}
                    className="text-slate-400 hover:text-[#E53935] transition-colors text-sm"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="font-heading text-lg font-bold uppercase tracking-wider text-white mb-6">
              Contact
            </h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-[#E53935] flex-shrink-0 mt-0.5" />
                <span className="text-slate-400 text-sm">
                  93 Hospital Rd. Sigma Building,<br />
                  Grand Cayman, Cayman Islands
                </span>
              </li>
              <li className="flex items-center gap-3">
                <Phone className="w-5 h-5 text-[#E53935] flex-shrink-0" />
                <a
                  href="tel:+13459289072"
                  className="text-slate-400 hover:text-white transition-colors text-sm"
                  data-testid="footer-phone"
                >
                  +1 (345) 928-9072
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-[#E53935] flex-shrink-0" />
                <a
                  href="mailto:info@anglinconsultants.ky"
                  className="text-slate-400 hover:text-white transition-colors text-sm"
                  data-testid="footer-email"
                >
                  info@anglinconsultants.ky
                </a>
              </li>
            </ul>

            {/* Social Links */}
            <div className="flex items-center gap-4 mt-6">
              <a
                href="https://linkedin.com"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-sm bg-[#1E293B] flex items-center justify-center text-slate-400 hover:bg-[#3B4B8C] hover:text-white transition-all"
                data-testid="social-linkedin"
                aria-label="LinkedIn"
              >
                <Linkedin className="w-5 h-5" />
              </a>
              <a
                href="https://twitter.com"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-sm bg-[#1E293B] flex items-center justify-center text-slate-400 hover:bg-[#3B4B8C] hover:text-white transition-all"
                data-testid="social-twitter"
                aria-label="Twitter"
              >
                <Twitter className="w-5 h-5" />
              </a>
              <a
                href="https://instagram.com"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-sm bg-[#1E293B] flex items-center justify-center text-slate-400 hover:bg-[#3B4B8C] hover:text-white transition-all"
                data-testid="social-instagram"
                aria-label="Instagram"
              >
                <Instagram className="w-5 h-5" />
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-[#1E293B]">
        <div className="section-container py-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-slate-500 text-sm">
            &copy; {currentYear} Anglin Sports Consultancy. All rights reserved.
          </p>
          <div className="flex items-center gap-6">
            <Link
              to="/privacy"
              className="text-slate-500 hover:text-slate-300 text-sm transition-colors"
            >
              Privacy Policy
            </Link>
            <Link
              to="/terms"
              className="text-slate-500 hover:text-slate-300 text-sm transition-colors"
            >
              Terms of Service
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
